// Faixa (banner) de status (DESIGN §5.22) e faixa fixa de offline.
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { useOnline } from "../lib/hooks";
import { Icones } from "./icones";

/** `neutra`: fundo recuo e ícone texto-2, para aviso fixo que não é estado (faixa de pontos das conquistas,
 *  "Você pode ver as conquistas"; docs/prototipo/conquistas-cadastro.html §4 e §12). */
export type TomFaixa = "info" | "alerta" | "erro" | "ok" | "neutra";

/** Variante `textoTinta`: fundo do tom e ícone no tom; o texto fica em tinta. */
const FUNDO: Record<TomFaixa, string> = {
  info: "bg-info-fundo [&>svg]:text-info",
  alerta: "bg-alerta-fundo [&>svg]:text-alerta",
  erro: "bg-erro-fundo [&>svg]:text-erro",
  ok: "bg-ok-fundo [&>svg]:text-ok",
  neutra: "bg-recuo [&>svg]:text-texto-2",
};

const TOM: Record<TomFaixa, { classe: string; icone: typeof Icones.info }> = {
  info: { classe: "bg-info-fundo text-info", icone: Icones.info },
  alerta: { classe: "bg-alerta-fundo text-alerta", icone: Icones.alerta },
  erro: { classe: "bg-erro-fundo text-erro", icone: Icones.erro },
  ok: { classe: "bg-ok-fundo text-ok", icone: Icones.sucesso },
  neutra: { classe: "bg-recuo text-tinta", icone: Icones.info },
};

export function Faixa({
  tom = "info",
  children,
  acao,
  textoTinta,
  acaoNoTopo,
  icone,
  className,
}: {
  tom?: TomFaixa;
  children: ReactNode;
  acao?: ReactNode;
  /** Ícone no lugar do padrão do tom (ex.: Carteira no pedido de transferência). */
  icone?: typeof Icones.info;
  /**
   * Texto em tinta e só o ícone no tom (px 14, gap 10). Para faixa com link de ação no meio do texto (o link
   * vermelho não fica bom sobre texto colorido). Mockup Equipe/Carteira, 28/09; DESIGN §5.22.
   */
  textoTinta?: boolean;
  /** Ação alinhada ao topo (botão "X" de 44 que fecha a faixa; mockup treino-nao-enviado §9). */
  acaoNoTopo?: boolean;
  className?: string;
}) {
  const { classe, icone: padrao } = TOM[tom];
  const Icone = icone ?? padrao;
  return (
    <div
      role={tom === "erro" ? "alert" : tom === "neutra" ? undefined : "status"}
      className={cn(
        "flex rounded-controle py-3 text-apoio",
        textoTinta ? cn("gap-2.5 px-3.5 text-tinta", FUNDO[tom]) : cn("gap-3 px-4", classe),
        className,
      )}
    >
      <Icone size={20} aria-hidden className={cn("shrink-0", !textoTinta && "mt-px")} />
      <div className="min-w-0 flex-1">{children}</div>
      {acao && <div className={cn("shrink-0", acaoNoTopo ? "self-start" : "self-center")}>{acao}</div>}
    </div>
  );
}

/** Faixa fixa no topo quando o aparelho fica sem conexão. */
export function FaixaOffline({ texto = "Sem conexão. Tentando de novo…" }: { texto?: string }) {
  const online = useOnline();
  if (online) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-60 bg-info-fundo px-margem pt-safe text-apoio text-info"
    >
      <p className="mx-auto flex max-w-app items-center gap-2 py-2">
        <Icones.info size={16} aria-hidden />
        {texto}
      </p>
    </div>
  );
}
